import React from "react";
import { captureException } from "@sentry/react";
import { Box, Typography, Button } from "@mui/material";

type FallbackRender = (props: { error: Error; reset: () => void }) => React.ReactNode;

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode | FallbackRender;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    captureException(error, { extra: { componentStack: info.componentStack } });
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      const { fallback } = this.props;

      if (fallback === null) {
        return null;
      }

      if (typeof fallback === "function") {
        return fallback({ error: this.state.error, reset: this.reset });
      }

      if (fallback !== undefined) {
        return fallback;
      }

      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={2}
          py={4}
        >
          <Typography variant="h6" color="error">
            Something went wrong
          </Typography>
          <Button variant="outlined" onClick={this.reset}>
            Try Again
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}
